import { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { RootState } from "../common/redux/store";
import { useNavigate } from "react-router-dom";
import { logout } from "../login/redux/authAction";
import { useAppDispatch, useAppSelector } from "../common/redux/reduxHooks";
import styled from "@emotion/styled";
import {
    Alert,
    Box,
    Button,
    Grid,
    Paper,
    Stack,
    TextField,
    Typography,
    useTheme,
} from "@mui/material";
import { UserStructureResponse } from "../structures/model/StructureModel";
import {
    getEmptyAvatarStructure,
    getPictureFromProfilImage,
} from "../utils/FormatUtils";
import { postUpdateProfil, StructureProfileUpdate } from "./redux/profilAction";
import { Modal } from "../common/components/Modal";
import { getUser, deleteUser } from "../common/redux/userAction";
import { storageService } from "../common/service/storageService";
import CountrySelect from "../common/components/CountryInput";
import { fetchPortalLink } from "../common/redux/subscriptionActions";

export const SocieteForm = () => {
    const userSelector: UserStructureResponse = useAppSelector(
        (state: RootState) => state.user.data,
    );
    const profilSelector: any = useAppSelector(
        (state: RootState) => state.profil,
    );
    const [account, setAccount] = useState<UserStructureResponse>(userSelector);

    const [errorMessages, setErrorMessages] = useState<{
        [K in keyof StructureProfileUpdate]?: string;
    }>({});

    const [isOpenConfirmModal, setIsOpenConfirmModal] =
        useState<boolean>(false);
    const [isOpenConfirmModalDelete, setIsOpenConfirmModalDelete] =
        useState<boolean>(false);
    const [isOpenErrormModal, setIsOpenErrorModal] = useState<boolean>(false);
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const theme = useTheme();
    const modalRef = useRef<HTMLDivElement | null>(null);

    const openSubscriptionPortal = async () => {
        try {
            const link = await fetchPortalLink();
            if (link) window.location.href = link;
        } catch (error) {
            console.error("Error fetching subscription portal link:", error);
        }
    };

    useEffect(() => {
        const idUser = storageService.getIdWithToken();
        if (idUser) dispatch(getUser(idUser));
    }, [dispatch]);

    useEffect(() => {
        const handleClose = (event: MouseEvent) => {
            if (
                modalRef.current &&
                !modalRef.current.contains(event.target as Node)
            ) {
                setIsOpenConfirmModalDelete(false);
            }
        };

        if (isOpenConfirmModalDelete) {
            document.addEventListener("mousedown", handleClose);
        } else {
            document.removeEventListener("mousedown", handleClose);
        }

        return () => {
            document.removeEventListener("mousedown", handleClose);
        };
    }, [isOpenConfirmModalDelete]);

    const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const data = new FormData(event.currentTarget);
        let validationMessages: typeof errorMessages = {};

        if (!data.get("societe"))
            validationMessages.societe = t("profileForm.validation.name");

        if (!account.country)
            validationMessages.country = t("profileForm.validation.country");

        if (Object.keys(validationMessages).length > 0) {
            setErrorMessages(validationMessages);
            return;
        }

        setErrorMessages({});

        dispatch(
            postUpdateProfil({
                description: data.get("description") as string,
                siret: data.get("siret") as string,
                societe: data.get("societe") as string,
                country: account.country
                    ? `/api/countries/${account.country.id}`
                    : "",
            }),
        ).then((res: any) => {
            if (res.error) {
                setIsOpenErrorModal(true);
                return;
            }

            setIsOpenConfirmModal(true);
            dispatch(getUser(account.id));
        });
    };

    const handleDeleteAccount = () => {
        dispatch(deleteUser()).then(() => {
            setIsOpenConfirmModalDelete(false);
            dispatch(logout());
            navigate("/login");
        });
    };

    return (
        <Box
            sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                width: "100%",
                marginTop: "2rem",
            }}
        >
            <Paper
                sx={{
                    maxWidth: theme.breakpoints.values.md,
                    backgroundColor: "#F8F9FA",
                    mr: "auto",
                    ml: "auto",
                    mt: 8,
                    mb: 3,
                    p: 5,
                    boxShadow:
                        "rgba(0, 0, 0, 0.4) 0px 2px 4px, rgba(0, 0, 0, 0.3) 0px 7px 13px -3px, rgba(0, 0, 0, 0.2) 0px -3px 0px inset",
                }}
                elevation={12}
            >
                <Box
                    sx={{
                        marginTop: 0,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                    }}
                >
                    <Box
                        sx={{
                            maxWidth: "300px",
                            maxHeight: "300px",
                            mt: "0px",
                        }}
                    >
                        {account.profileImage && (
                            <img
                                src={getPictureFromProfilImage(
                                    account.profileImage.contentUrl,
                                )}
                                style={{
                                    maxWidth: "200px",
                                    maxHeight: "200px",
                                }}
                                alt="Profil"
                            />
                        )}
                        {!account.profileImage && (
                            <img
                                src={getEmptyAvatarStructure()}
                                style={{
                                    maxWidth: "200px",
                                    maxHeight: "200px",
                                }}
                                alt="Profil"
                            />
                        )}
                    </Box>
                    {account.subscription?.active && (
                        <Box
                            sx={{
                                width: "100%",
                                display: "flex",
                                justifyContent: "space-between",
                                mb: 2,
                                mt: 2,
                            }}
                        >
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    minWidth: 220,
                                    mr: 2,
                                }}
                            >
                                <Typography variant="subtitle1" sx={{ mr: 1 }}>
                                    {t(
                                        "profileForm.subscription.subscriptionPlan",
                                    )}
                                    :
                                </Typography>
                                <Box
                                    sx={{
                                        backgroundColor:
                                            theme.palette.secondary.main,
                                        color: theme.palette.primary
                                            .contrastText,
                                        borderRadius: "4px",
                                        px: 2,
                                        py: 0.5,
                                        fontWeight: "bold",
                                        fontSize: "0.95rem",
                                        display: "inline-block",
                                    }}
                                >
                                    {
                                        account.subscription.subscriptionPlan
                                            ?.name
                                    }
                                </Box>
                            </Box>
                            {!account.subscription.subscriptionPlan
                                ?.internal && (
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    onClick={openSubscriptionPortal}
                                    sx={{ minWidth: 220 }}
                                >
                                    {t(
                                        "profileForm.subscription.manageSubscription",
                                    )}
                                </Button>
                            )}
                        </Box>
                    )}
                    <Box
                        component="form"
                        noValidate
                        onSubmit={handleSubmit}
                        sx={{ mt: 3 }}
                    >
                        <Box
                            sx={{
                                mb: 4,
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                flexDirection: "column",
                            }}
                        >
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        required
                                        fullWidth
                                        id="societe"
                                        label={t("sign.up.organization")}
                                        name="societe"
                                        autoComplete="organization"
                                        defaultValue={account.societe}
                                        error={!!errorMessages.societe}
                                        helperText={
                                            errorMessages.societe
                                                ? errorMessages.societe
                                                : ""
                                        }
                                    />
                                </Grid>
                                {account.country?.code === "fr" && (
                                    <Grid item xs={12}>
                                        <TextField
                                            required
                                            fullWidth
                                            id="siret"
                                            label={t("sign.up.siret")}
                                            name="siret"
                                            autoComplete="tel-national"
                                            defaultValue={account.siret}
                                        />
                                    </Grid>
                                )}
                                <Grid item xs={12}>
                                    <TextField
                                        required
                                        fullWidth
                                        id="telephone"
                                        label={t("sign.up.phone")}
                                        name="telephone"
                                        autoComplete="tel-national"
                                        defaultValue={account.telephone}
                                        disabled
                                    />
                                    <Box sx={{ mt: 1 }}>
                                        <em>
                                            {t("profileForm.disabled.phone")}
                                        </em>
                                    </Box>
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        margin="normal"
                                        required
                                        fullWidth
                                        id="email"
                                        label={t("auth.email")}
                                        name="email"
                                        autoComplete="email"
                                        defaultValue={account.email}
                                        disabled
                                    />
                                    <Box sx={{ mt: 1 }}>
                                        <em>
                                            {t("profileForm.disabled.email")}
                                        </em>
                                    </Box>
                                </Grid>
                                <Grid item xs={12}>
                                    <CountrySelect
                                        value={account.country}
                                        onChange={(country) => {
                                            setAccount((prev) => ({
                                                ...prev,
                                                country,
                                            }));
                                        }}
                                        errorMessage={errorMessages.country}
                                        required={true}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        placeholder="Description"
                                        multiline
                                        label="Description"
                                        fullWidth
                                        defaultValue={account.description}
                                        minRows={2}
                                        id="description"
                                        name="description"
                                        maxRows={4}
                                    />
                                </Grid>
                            </Grid>

                            <BtnContainer>
                                <UpdateBtn type="submit" variant="contained">
                                    {t("button.update.profile")}
                                </UpdateBtn>
                                <DeleteBtn
                                    onClick={() =>
                                        setIsOpenConfirmModalDelete(true)
                                    }
                                    variant="contained"
                                >
                                    {t("button.delete.profile")}
                                </DeleteBtn>
                            </BtnContainer>
                        </Box>
                    </Box>

                    {profilSelector.error && (
                        <Alert severity="error" className="errorMessage">
                            {profilSelector.error?.response?.data.message}
                        </Alert>
                    )}
                    <div ref={modalRef}>
                        <Modal
                            open={isOpenConfirmModalDelete}
                            onValidation={() => handleDeleteAccount()}
                            onClose={() => setIsOpenConfirmModalDelete(false)}
                            title={t("modal.delete.profile.title")}
                            message={t("modal.delete.profile.description")}
                            titleButtonValidation={t("button.delete.project")}
                        />
                    </div>
                    <Modal
                        open={isOpenConfirmModal}
                        onValidation={() => setIsOpenConfirmModal(false)}
                        onClose={() => setIsOpenConfirmModal(false)}
                        title={t("modal.update.profile.title")}
                        message={t("modal.update.profile.description")}
                        titleButtonValidation={t("button.modal.valid")}
                    />

                    <Modal
                        open={isOpenErrormModal}
                        onValidation={() => setIsOpenErrorModal(false)}
                        onClose={() => setIsOpenErrorModal(false)}
                        title={t("profileForm.errorModal.title")}
                        message={t("profileForm.errorModal.description")}
                        titleButtonValidation={t(
                            "profileForm.errorModal.button",
                        )}
                    />
                </Box>
            </Paper>
        </Box>
    );
};

const UpdateBtn = styled(Button)({
    width: "180px",
    padding: "0.5rem",
    borderRadius: "10px",
    backgroundImage:
        "linear-gradient(to bottom, #b5e550, #abd854, #a1cb58, #97be5a, #8eb15c)",
    textTransform: "uppercase",
    fontSize: "14px !important",
});

const DeleteBtn = styled(Button)({
    width: "180px",
    padding: "0.5rem",
    borderRadius: "10px",
    backgroundImage:
        "linear-gradient(to right bottom, #ff0000, #ff322a, #ff4c46, #ff625f, #ff7676)",
    textTransform: "uppercase",
    fontSize: "14px !important",
});

const BtnContainer = styled(Stack)({
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    width: "100%",
    gap: "1rem",
    marginTop: "2rem",

    "@media(width >= 768px)": {
        flexDirection: "row",
        gap: "2rem",
    },
});
